import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../lib/api';
import { cn } from '../lib/utils';

interface Partner {
  id: string;
  name: string;
  adapter_type: string;
  backend_url: string;
  auth_type: string;
  auth_header_name: string | null;
  auth_query_param_name: string | null;
  enabled: boolean;
  tenant_count: string;
  created_at: string;
}

type AuthType = 'none' | 'basic' | 'bearer' | 'api_key_header' | 'api_key_query';

const AUTH_TYPE_OPTIONS: Array<{ value: AuthType; label: string; hint: string }> = [
  { value: 'basic', label: 'Basic Auth', hint: 'HTTP Basic — Authorization: Basic base64(user:pass)' },
  { value: 'bearer', label: 'Bearer Token', hint: 'Authorization: Bearer <token>' },
  { value: 'api_key_header', label: 'API Key (header)', hint: 'Custom header (e.g. X-API-Key: <token>)' },
  { value: 'api_key_query', label: 'API Key (URL param)', hint: 'Token appended as ?api_key=<token>' },
  { value: 'none', label: 'No Auth', hint: 'No credentials sent (use only for open endpoints)' },
];

interface FormData {
  id: string;
  name: string;
  adapter_type: string;
  backend_url: string;
  auth_type: AuthType;
  auth_username: string;
  auth_password: string;
  auth_token: string;
  auth_header_name: string;
  auth_query_param_name: string;
  enabled: boolean;
}

const emptyForm: FormData = {
  id: '', name: '', adapter_type: 'generic', backend_url: '',
  auth_type: 'basic',
  auth_username: '', auth_password: '',
  auth_token: '',
  auth_header_name: 'X-API-Key',
  auth_query_param_name: 'api_key',
  enabled: true,
};

export const PartnersPage: React.FC = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPartners = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await apiCall('/api/config/partners');
      if (resp.ok) { const d = await resp.json(); setPartners(d.partners || []); }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPartners(); }, [loadPartners]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setError(null); setShowForm(true); };
  const openEdit = (p: Partner) => {
    setEditingId(p.id);
    setForm({
      id: p.id,
      name: p.name,
      adapter_type: p.adapter_type,
      backend_url: p.backend_url,
      auth_type: (p.auth_type as AuthType) || 'basic',
      auth_username: '',
      auth_password: '',
      auth_token: '',
      auth_header_name: p.auth_header_name || 'X-API-Key',
      auth_query_param_name: p.auth_query_param_name || 'api_key',
      enabled: p.enabled,
    });
    setError(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const url = editingId ? `/api/config/partners/${editingId}` : '/api/config/partners';
      const body: any = {
        id: form.id,
        name: form.name,
        adapter_type: form.adapter_type,
        backend_url: form.backend_url,
        auth_type: form.auth_type,
        enabled: form.enabled,
      };

      switch (form.auth_type) {
        case 'basic':
          if (form.auth_username || !editingId) body.auth_username = form.auth_username;
          if (form.auth_password || !editingId) body.auth_password = form.auth_password;
          break;
        case 'bearer':
          if (form.auth_token || !editingId) body.auth_token = form.auth_token;
          break;
        case 'api_key_header':
          body.auth_header_name = form.auth_header_name;
          if (form.auth_token || !editingId) body.auth_token = form.auth_token;
          break;
        case 'api_key_query':
          body.auth_query_param_name = form.auth_query_param_name;
          if (form.auth_token || !editingId) body.auth_token = form.auth_token;
          break;
        case 'none':
          break;
      }

      const resp = await apiCall(url, { method: editingId ? 'PUT' : 'POST', body: JSON.stringify(body) });
      if (!resp.ok) { const d = await resp.json(); throw new Error(d.error || 'Failed'); }
      setShowForm(false);
      loadPartners();
    } catch (err: any) { setError(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(`Delete partner "${id}" and all its tenant assignments?`)) return;
    await apiCall(`/api/config/partners/${id}`, { method: 'DELETE' });
    loadPartners();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partners</h1>
          <p className="text-sm text-gray-500 mt-1">Manage integration partners and their tenant assignments.</p>
        </div>
        <button onClick={openCreate} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          Add Partner
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold">{editingId ? `Edit: ${editingId}` : 'New Partner'}</h2>
          {error && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!editingId && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Partner ID</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm" value={form.id} onChange={e => setForm(f => ({ ...f, id: e.target.value }))} placeholder="partner" />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Backend URL</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm" value={form.backend_url} onChange={e => setForm(f => ({ ...f, backend_url: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Adapter Type</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.adapter_type} onChange={e => setForm(f => ({ ...f, adapter_type: e.target.value }))}>
                <option value="generic">Generic</option>
                <option value="partner">PARTNER</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Authentication</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={form.auth_type}
                onChange={e => setForm(f => ({ ...f, auth_type: e.target.value as AuthType }))}
              >
                {AUTH_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                {AUTH_TYPE_OPTIONS.find(o => o.value === form.auth_type)?.hint}
              </p>
            </div>

            {form.auth_type === 'basic' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Username {editingId && <span className="text-gray-400">(leave blank to keep current)</span>}
                  </label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm" value={form.auth_username} onChange={e => setForm(f => ({ ...f, auth_username: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Password {editingId && <span className="text-gray-400">(leave blank to keep current)</span>}
                  </label>
                  <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm" value={form.auth_password} onChange={e => setForm(f => ({ ...f, auth_password: e.target.value }))} />
                </div>
              </>
            )}

            {form.auth_type === 'bearer' && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Bearer Token {editingId && <span className="text-gray-400">(leave blank to keep current)</span>}
                </label>
                <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm font-mono" value={form.auth_token} onChange={e => setForm(f => ({ ...f, auth_token: e.target.value }))} />
              </div>
            )}

            {form.auth_type === 'api_key_header' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Header Name</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm font-mono" placeholder="X-API-Key" value={form.auth_header_name} onChange={e => setForm(f => ({ ...f, auth_header_name: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Token {editingId && <span className="text-gray-400">(leave blank to keep current)</span>}
                  </label>
                  <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm font-mono" value={form.auth_token} onChange={e => setForm(f => ({ ...f, auth_token: e.target.value }))} />
                </div>
              </>
            )}

            {form.auth_type === 'api_key_query' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Query Parameter Name</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm font-mono" placeholder="api_key" value={form.auth_query_param_name} onChange={e => setForm(f => ({ ...f, auth_query_param_name: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Token {editingId && <span className="text-gray-400">(leave blank to keep current)</span>}
                  </label>
                  <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm font-mono" value={form.auth_token} onChange={e => setForm(f => ({ ...f, auth_token: e.target.value }))} />
                </div>
              </>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Partner ID</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Name</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Adapter</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Backend URL</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Tenants</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Status</th>
                <th className="text-right px-5 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map(p => (
                <tr key={p.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/configuration/partners/${p.id}`)}>
                  <td className="px-5 py-3 font-mono text-gray-700">{p.id}</td>
                  <td className="px-5 py-3 font-medium text-gray-900">{p.name}</td>
                  <td className="px-5 py-3"><span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{p.adapter_type}</span></td>
                  <td className="px-5 py-3 text-gray-600 truncate max-w-[200px]">{p.backend_url}</td>
                  <td className="px-5 py-3">
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded', parseInt(p.tenant_count) > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500')}>
                      {p.tenant_count}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded', p.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500')}>
                      {p.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right space-x-2">
                    <button onClick={(e) => { e.stopPropagation(); navigate(`/configuration/partners/${p.id}`); }} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Detail</button>
                    <button onClick={(e) => { e.stopPropagation(); openEdit(p); }} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Edit</button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} className="text-red-600 hover:text-red-800 text-xs font-medium">Delete</button>
                  </td>
                </tr>
              ))}
              {partners.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-gray-400">No partners configured.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
